import { fetchRefreshToken } from '@client-shared/api/refresh-token.api'
import describeResponseBody from '@client-shared/utils/describe-response-body'
import isOnLocalhost from '@client-shared/utils/is-on-localhost'
import dayjs from 'dayjs'
import { jwtDecode } from 'jwt-decode'
import Cookies from 'universal-cookie'
import { reactive, computed } from 'vue'

import constants from '@/config/constants'
import i18nInstance from '@/plugins/i18n'
import sendRaygun from '@/utils/send-raygun'

const accessTokenKey = constants.LOCAL_STORAGE_ACCESS_TOKEN_KEY
const refreshTokenKey = constants.LOCAL_STORAGE_REFRESH_TOKEN_KEY

// Confirming 2FA and changing the password make the server invalidate every older token and answer with a new
// pair. A 401 for another request that arrives before that answer is applied still matches the token the app holds,
// although its end is exactly what the rotation causes: error-handler.js and auth-handler.js wait for a rotation
// before they decide whether a 401 or a failed refresh ends the session. Only for one whose request is on the wire
// (isRequestSent): a rotation still queued behind a token refresh waits for that refresh in turn, and a refresh
// failing then means the session really ended. Module scope, not part of the reactive auth object: Vue must not
// proxy a promise.
// 🚨 Has to match ionsrc/plugins/auth.js
let pendingTokenRotation = null

const cookies = new Cookies()
const $i18n = i18nInstance.global

const abortControllers = {}

const getAccessToken = (key) => {
  // get it from cookie first
  let token = cookies.get(key)

  // legacy fallback - get existing cookie from localStorage
  if (!token) {
    token = window.localStorage.getItem(key)
  }

  try {
    // ensure token format is valid by trying to decoding it
    jwtDecode(token)

    return token
  } catch {}
}

const setAuthCookie = ({
  key,
  token,
}) => {
  cookies.set(key, token, {
    path: '/',
    expires: dayjs()
      .add(1, 'year')
      .toDate(),
    secure: !isOnLocalhost,
    sameSite: isOnLocalhost ? 'lax' : 'strict',
  })
}

const removeAuthCookie = ({
  key,
}) => {
  cookies.remove(key, {
    path: '/',
    secure: !isOnLocalhost,
    sameSite: isOnLocalhost ? 'lax' : 'strict',
  })
}

export default {
  install: (app) => {
    const $axios = app.config.globalProperties.$axios
    const $store = app.config.globalProperties.$store
    const $router = app.config.globalProperties.$router

    // The logout currently in flight, shared by every caller that arrives while it runs. See logout().
    let logoutPromise

    const auth = reactive({
      loggedIn: computed(() => Boolean(auth.user)),
      accessToken: getAccessToken(accessTokenKey),
      refreshToken: getAccessToken(refreshTokenKey),
      user: undefined,

      // Counts logins and logouts, not token refreshes or rotations: auth-handler.js resumes a request queued behind a
      // failed refresh only within the session it was made in. The user id cannot tell that: after a logout and another
      // login to the same account it still matches.
      // 🚨 Has to match ionsrc/plugins/auth.js
      sessionGeneration: 0,

      login: async function ({ email, password, totp }) {
        const { data } = await $axios.post('login', {
          email,
          password,
          totp,
        },
        {
          headers: {
            Authorization: null,
          },
        })

        auth.sessionGeneration += 1
        auth.setAccessToken(data.access_token)
        auth.setRefreshToken(data.refresh_token)

        if (auth.accessToken) {
          await auth.fetchUser()
        }

        return data
      },

      fetchUser: async function () {
        if (abortControllers.fetchUser instanceof AbortController) {
          abortControllers.fetchUser.abort()
        }

        // Kept per call so the check below can tell whether THIS request was superseded: an abort
        // that reaches axios rejects and is caught below, but one that lands after the response
        // resolved does not — and that older user must not overwrite the newer request's result.
        const controller = new AbortController()

        abortControllers.fetchUser = controller

        try {
          const response = await $axios.get('current-user', {
            signal: controller.signal,
          })

          if (controller.signal.aborted) {
            return
          }

          // A 200 without a usable user (an empty body from a proxy/captive portal, or a connection
          // torn down while the tab was restored from the background) must never clear the session.
          // Unlike ionic/ this guard has always been here, so nobody is logged out by it — the
          // report exists to tell us whether web users hit the same intermediaries.
          // _id is the invariant the callers rely on ($auth.user._id), so an empty or malformed
          // user object has to fail the guard just like a missing one.
          if (!response.data?.user?._id) {
            sendRaygun({
              error: new Error('EMPTY_CURRENT_USER_RESPONSE_DATA'),
              tags: [ 'auth-fetch-user' ],
              // Narrows down which cause fired: classification 'preflight-ok' points at the CORS
              // preflight being served for the GET, 'markup' at a captive portal / AV middlebox,
              // 'empty' at the connection being torn down, 'json-like' at a truncated payload, and
              // an object body at a genuine shape change. visibilityState is the web analogue of
              // ionic's msSinceAppResume — a tab restored from the background hits the same race.
              customData: {
                httpStatus: response.status,
                contentType: response.headers?.['content-type'],
                body: describeResponseBody(response.data),
                isOnline: window.navigator.onLine,
                visibilityState: window.document.visibilityState,
              },
            })

            return
          }

          auth.setUser(response.data.user)
        } catch (err) {
          // every route change refetches the user and aborts the previous request, so a cancel here
          // is the normal case, not an error worth logging
          if ($axios.isCancel(err)) {
            return
          }

          console.error(err)
        }
      },

      setUser: function (user) {
        auth.user = user
        $store.commit('currentUser/SET_USER', user)

        if (!user) {
          // set anonymous raygun user identifier
          if (window.rg4js) {
            window.rg4js('endSession')
            window.rg4js('setUser', {
              isAnonymous: true,
            })
          }
        } else if (user?._id) {
          // set anonymous raygun user identifier
          if (window.rg4js) {
            window.rg4js('setUser', {
              identifier: user._id,
              isAnonymous: false,
            })
          }
        }
      },

      setAccessToken: function (token) {
        auth.accessToken = token

        if (!token) {
          removeAuthCookie({ key: accessTokenKey })
          window.localStorage.removeItem(accessTokenKey)

          return
        }

        setAuthCookie({
          key: accessTokenKey,
          token,
        })
        window.localStorage.setItem(accessTokenKey, token)
      },

      setRefreshToken: function (token) {
        auth.refreshToken = token

        if (!token) {
          removeAuthCookie({ key: refreshTokenKey })
          window.localStorage.removeItem(refreshTokenKey)

          return
        }

        setAuthCookie({
          key: refreshTokenKey,
          token,
        })
        window.localStorage.setItem(refreshTokenKey, token)
      },

      // See pendingTokenRotation. Returns the rotation request's result once the new tokens are applied.
      replaceTokensFrom: async function (rotationRequest) {
        const rotation = {
          isRequestSent: false,
          promise: (async () => {
            const result = await rotationRequest

            await auth.setAccessToken(result.authTokens.access_token)
            await auth.setRefreshToken(result.authTokens.refresh_token)

            return result
          })(),
        }

        pendingTokenRotation = rotation

        try {
          return await rotation.promise
        } finally {
          if (pendingTokenRotation === rotation) {
            pendingTokenRotation = null
          }
        }
      },

      // Called by auth-handler.js once the rotation request has passed the refresh step and goes out
      markTokenRotationRequestSent: function () {
        if (pendingTokenRotation) {
          pendingTokenRotation.isRequestSent = true
        }
      },

      // Resolves to whether a sent rotation completed, i.e. replaced the tokens
      waitForSentTokenRotation: async function () {
        if (!pendingTokenRotation?.isRequestSent) {
          return false
        }

        return pendingTokenRotation.promise
          .then(() => true)
          .catch(() => false)
      },

      getNewRefreshToken: async function () {
        try {
          const response = await fetchRefreshToken({
            $axios,
            token: auth.refreshToken,
          })

          if (!response?.data) {
            throw new Error('EMPTY_FETCH_REFRESH_TOKEN_RESPONSE_DATA')
          }

          if (response?.data?.access_token && response?.data?.refresh_token) {
            auth.setAccessToken(response.data.access_token)
            auth.setRefreshToken(response.data.refresh_token)
          }
        } catch (error) {
          await sendRaygun({
            error,
            tags: [ 'fetch-refresh-token-failed' ],
          })

          // Rethrown, not swallowed. Swallowing resolved as if the refresh had worked, so
          // auth-handler.js's catch - the branch that logs out when a refresh fails - could never run
          // and its `token-refresh-failed` tag never fired. It also left the two apps disagreeing on a
          // path their files say has to match: ionic's getNewRefreshToken has always rejected.
          //
          // Safe now that logout() is idempotent. Before that, making this reachable meant a second
          // logout racing the one the 401 error handler performs, and the later call rebuilt its
          // redirect from a pathname the first had already changed to /login - which broke
          // xhr-error-handling/401.spec.js.
          throw error
        }
      },

      // 🚨 Has to match ionsrc/plugins/auth.js
      //
      // A burst of 401s calls this once per failing request. The default branch at the bottom builds
      // its "come back here afterwards" redirect from window.location.pathname - which the FIRST
      // logout has already changed to /login by the time a second one reads it, so the later call
      // overwrites the redirect that would have returned the user to the page they were on.
      // xhr-error-handling/401.spec.js asserts exactly that return trip.
      //
      // So share one logout: the first call does the work, every call that arrives while it is still
      // running awaits the same promise instead of navigating again. Cleared on settle, so a later,
      // genuine logout still runs.
      logout: async function ({ shouldBroadcast = true, redirectTarget } = {}) {
        if (logoutPromise) {
          return logoutPromise
        }

        logoutPromise = auth.runLogout({
          shouldBroadcast,
          redirectTarget,
        })
          .finally(() => {
            logoutPromise = undefined
          })

        return logoutPromise
      },

      runLogout: async function ({ shouldBroadcast = true, redirectTarget } = {}) {
        const $broadcastChannel = app.config.globalProperties.$broadcastChannel
        const $config = app.config.globalProperties.$config

        const email = auth.user?.email

        // Captured before anything below can navigate: it is what the default redirect is built from,
        // and the route guard can move the app while the wait and the token clears happen.
        const redirectPathname = window.location.pathname

        // Credentials first, ahead of the sync wait below. They are what makes a request sendable, so
        // anything that leaves during the wait carries a token we have already decided is dead. The
        // wait is for pending store writes (clipboard entries), which do not need the tokens - and
        // under a faked clock it never fires at all, so gating the clear on it left them in place
        // indefinitely.
        //
        // Also safe only now: requests in that window 401, reach the error handler and call logout
        // again, which used to clobber the first logout's redirect.
        auth.sessionGeneration += 1
        auth.setAccessToken(undefined)
        auth.setRefreshToken(undefined)

        await new Promise(resolve => setTimeout(resolve, constants.VUEX_TO_LOCALSTORAGE_SYNC_DELAY_IN_MS)) // Wait until pending VUEX store updates are written (deleted, pending clipboard entries must be saved to local storage)

        auth.setUser(undefined)

        if (shouldBroadcast) {
          $broadcastChannel.postMessage($config.constants.BROADCAST_MESSAGES.LOGOUT)
        }

        if ($config.isDemoserver && !window.Cypress) {
          window.location.href = app.config.globalProperties.$utm($i18n.t('data.pf_links.planfred_website'), 'demoserver-logout-website-redirect')

          return
        }

        if (redirectTarget) {
          return $router.push(redirectTarget)
        }

        return $router.push({
          name: 'login',
          query: {
            redirect: redirectPathname,
            sessionExpired: true,
            email,
          },
        })
      },
    })

    $router.beforeEach(async (to, from) => {
      if (to.meta.layout === 'sharing') {
        // The sharing layout only uses sharingAuthToken authentication and needs no redirect or user-fetch handling
        return
      }

      if (!auth.loggedIn) {
        await auth.fetchUser()
      }

      if (to.meta.layout !== 'public' && !auth.loggedIn) {
        return $router.push({
          name: 'login',
          query: {
            redirect: to.fullPath,
            sessionExpired: true,
          },
        })
      }

      if (to.meta.layout === 'public' && auth.loggedIn && !to.meta.canVisitAsLoggedIn) {
        return $router.push(to.query.redirect || { name: 'projects' })
      }
    })

    app.config.globalProperties.$auth = auth
    app.provide('$auth', auth)
  },
}
